// ---------------------------------------------------------------------------
// The room's door: who may still come in, and the one line a host needs before
// they change it.
//
// **It is at the top of the player list rather than in the settings column**,
// and that is what makes it the headline control it is meant to be. It is not a
// rule of the game — it changes nothing anybody scores — so it does not belong
// among the clocks; what it decides is who is in the list underneath it, which
// is what it is sitting on top of.
//
// **One control with three answers, where there were two switches.** `roomLocked`
// and `allowSpectators` were two booleans over three states anybody wanted, and
// the fourth combination — refusing watchers while still handing out places —
// was a shape nobody asked for that every reader had to handle. Worse, reaching
// the shut state meant pressing two things in an order that briefly refused
// everybody with neither control saying so. See `GameSettings.roomLock`.
//
// A guest sees this only when the door is not open, and then as a fact rather
// than a control: it is the answer to "why am I watching", and a switch that
// always refuses is decision 4 at the top of `LobbyScreen.tsx`.
// ---------------------------------------------------------------------------

import type { ReactElement } from 'react';
import type { RoomLock } from '@countdown/shared';
import { Segmented } from '../../../components/primitives/index.js';
import { Icon, LockIcon } from '../../../components/primitives/Icon.js';
import { DOOR_EXPLAINER, DOOR_HINT, DOOR_LABEL, DOOR_OPTIONS } from './copy.js';

export function RoomDoor({
  lock,
  host,
  onChange,
}: {
  lock: RoomLock;
  host: boolean;
  onChange: (lock: RoomLock) => void;
}): ReactElement | null {
  if (!host) {
    // Nothing at all in an open room. "This room is not locked" is not news to
    // anybody who has just walked into it.
    if (lock === 'open') return null;
    return (
      <div
        data-testid="room-door"
        data-room-lock={lock}
        className="flex items-start gap-2.5 rounded-card border border-line bg-surface px-3.5 py-3 text-[13px] text-ink-muted"
      >
        <Icon icon={LockIcon} size={16} className="mt-0.5 shrink-0" />
        <p>
          <span className="font-semibold text-ink">
            {lock === 'room' ? 'Room closed.' : 'Players locked.'}
          </span>{' '}
          {DOOR_HINT[lock]}
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="room-door"
      data-room-lock={lock}
      className={`flex flex-col gap-2 rounded-card border bg-surface px-3.5 py-3 transition-colors duration-200 ease-out ${
        lock === 'open' ? 'border-line' : 'border-line-strong'
      }`}
    >
      <div className="flex items-center gap-2.5">
        <Icon
          icon={LockIcon}
          size={16}
          className={`shrink-0 ${lock === 'open' ? 'text-ink-dim' : 'text-ink'}`}
        />
        <span className="text-[14px] font-medium text-ink">{DOOR_LABEL}</span>
      </div>

      <Segmented
        label={DOOR_LABEL}
        size="sm"
        value={lock}
        options={DOOR_OPTIONS}
        onChange={onChange}
      />

      {/* The state the room is in now, in the present tense. Three conditional
          sentences about a hypothetical press is a paragraph pretending to be a
          control; one sentence about what is true is a label. */}
      <p data-testid="door-hint" className="text-[12px] text-ink-muted">
        {DOOR_HINT[lock]}
      </p>

      {/* What the two places in the room ARE. Every string above assumes it, and
          it stays one line. */}
      <p data-testid="door-explainer" className="border-t border-line pt-2 text-[12px] text-ink-dim">
        {DOOR_EXPLAINER}
      </p>
    </div>
  );
}
